import { faArrowsRepeat } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { chain, concat } from 'lodash';
import React, { Dispatch, SetStateAction, useMemo, useState } from 'react';
import VoxelDialog from '../../../components/voxel/Dialogs/VoxelDialog';
import VoxelPopoverMenuItem from '../../../components/voxel/PopoverMenu/VoxelPopoverMenuItem';
import VoxelAvatar from '../../../components/voxel/VoxelAvatar';
import VoxelAdvancedTextSearch from '../../../components/voxel/Search/VoxelAdvancedTextSearch';
import { CurrentTeamGq } from '../../../graphql/users/models/CurrentTeamGq';
import { PublicUserGq } from '../../../graphql/users/models/PublicUserGq';
import VoxelDialogContent from '../../../components/voxel/Dialogs/VoxelDialogContent';
import { useActiveTabContext } from '../../../logic/activeTabContext/useActiveTabContext';
import VoxelMenuItem from '../../../components/voxel/PopoverMenu/VoxelMenuItem';
import VoxelSelectItem from '../../../components/voxel/Forms/VoxelSelectItem';

export interface SwitchTeamDialogProps {
  team: CurrentTeamGq;
  availableUsers: PublicUserGq[];
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
}

export const SwitchTeamDialog: React.FC<SwitchTeamDialogProps> = ({
  team,
  availableUsers,
  open,
  setOpen,
}) => {
  const [textSearch, setTextSearch] = useState<string>('');
  const activeTabContext = useActiveTabContext();

  const alternativeUsers = useMemo(
    () =>
      chain(availableUsers)
        .filter((au) => au.team.id !== team.id)
        .filter(
          (au) =>
            textSearch.length === 0 ||
            au.team.name?.toLowerCase().includes(textSearch.toLowerCase())
        )
        .orderBy((au) => au.team.name)
        .value(),
    [team, availableUsers, textSearch]
  );

  const allUsers = useMemo(
    () =>
      concat(
        availableUsers.find((t) => t.team.id === team.id),
        alternativeUsers
      ),
    [team, availableUsers, textSearch]
  );

  return (
    <VoxelDialog
      open={open}
      onClose={() => setOpen(false)}
      title={'Switch team account'}
      titleIcon={<FontAwesomeIcon icon={faArrowsRepeat} />}
      subtitle={'You can only switch to team accounts where you are a member'}
    >
      <VoxelDialogContent>
        <div className="pb-2">
          <VoxelAdvancedTextSearch
            placeholder="Team name"
            size="sm"
            value={textSearch}
            onChange={(event) => setTextSearch(event.target.value)}
            deleteValue={() => setTextSearch('')}
          />
        </div>
        <div className="w-full flex flex-col h-96 max-h-96 min-h-max overflow-y-auto">
          {!!alternativeUsers.length && (
            <div className="py-4 w-full">
              {allUsers.map((au, index) => (
                <VoxelSelectItem
                  key={au.id}
                  selected={index === 0}
                  onClick={() => activeTabContext.setCurrentTeamId(au.team.id)}
                  active={false}
                >
                  <div className="flex flex-row items-center gap-4 px-1.5 py-0.5">
                    <VoxelAvatar name={au.team.name} size="sm" />
                    {au.team.name}
                  </div>
                </VoxelSelectItem>
              ))}
            </div>
          )}
        </div>
      </VoxelDialogContent>
    </VoxelDialog>
  );
};

export default SwitchTeamDialog;
